import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { MagnifyingGlass, CalendarBlank, ClipboardText, ChatCircleText, Barbell } from "@phosphor-icons/react";
import ClientDetailDrawer from "./ClientDetailDrawer";

const STATUSES = ["All", "Active", "Onboarding", "Inactive"];
const STATUS_STYLES = {
  Active: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40",
  Onboarding: "bg-amber-500/15 text-amber-400 border-amber-500/40",
  Inactive: "bg-zinc-500/15 text-zinc-400 border-zinc-500/40",
};

export default function ClientsPanel({ onMessageClient }) {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [drawerClient, setDrawerClient] = useState(null);
  const [drawerTab, setDrawerTab] = useState("plan");

  useEffect(() => {
    api.get("/admin/clients").then(({ data }) => setClients(data)).catch(() => {});
  }, []);

  const openDrawer = (client, tab) => {
    setDrawerClient(client);
    setDrawerTab(tab);
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return clients.filter((c) => {
      const matchesStatus = statusFilter === "All" || c.status === statusFilter;
      const matchesSearch = !term || c.name?.toLowerCase().includes(term) || c.email?.toLowerCase().includes(term);
      return matchesStatus && matchesSearch;
    });
  }, [clients, search, statusFilter]);

  return (
    <div data-testid="admin-clients-panel">
      <h1 className="font-heading text-3xl text-white tracking-tight mb-6">Clients</h1>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#71717A]" />
          <input
            data-testid="clients-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className="w-full bg-[#141414] border border-[#27272A] rounded-full pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-[#71717A]"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {STATUSES.map((s) => (
            <button
              key={s}
              data-testid={`client-status-filter-${s.toLowerCase()}`}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                statusFilter === s ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#27272A] text-[#A1A1AA] hover:border-white/30"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((c) => (
          <div key={c.id} data-testid={`client-card-${c.id}`} className="bg-[#141414] border border-[#27272A] rounded-2xl p-5 flex flex-col">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-white font-semibold">{c.name}</p>
                <p className="text-xs text-[#71717A]">{c.email}</p>
              </div>
              <span data-testid={`client-status-badge-${c.id}`} className={`text-[10px] font-semibold uppercase px-2.5 py-1 rounded-full border shrink-0 ${STATUS_STYLES[c.status]}`}>
                {c.status}
              </span>
            </div>

            <div className="space-y-1.5 text-xs text-[#A1A1AA] mb-4">
              <p className="flex items-center gap-1.5">
                <CalendarBlank size={13} className="text-[#71717A]" /> Joined{" "}
                {new Date(c.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </p>
              <p data-testid={`client-package-${c.id}`}>{c.current_package_name || "No Active Package"}</p>
              <p data-testid={`client-parq-status-${c.id}`} className={c.parq_complete ? "text-emerald-400" : "text-amber-400"}>
                {c.parq_complete ? "PAR-Q Complete" : "PAR-Q Pending"}
              </p>
            </div>

            <div className="mt-auto flex flex-wrap gap-2">
              <button
                data-testid={`client-workout-plan-${c.id}`}
                onClick={() => openDrawer(c, "plan")}
                className="flex items-center gap-1.5 text-xs bg-white/5 hover:bg-white/10 text-white rounded-full px-3 py-1.5 transition-colors"
              >
                <Barbell size={13} /> Workout Plan
              </button>
              <button
                data-testid={`client-view-parq-${c.id}`}
                onClick={() => openDrawer(c, "waiver")}
                className="flex items-center gap-1.5 text-xs bg-white/5 hover:bg-white/10 text-white rounded-full px-3 py-1.5 transition-colors"
              >
                <ClipboardText size={13} /> View PAR-Q
              </button>
              <button
                data-testid={`client-message-${c.id}`}
                onClick={() => onMessageClient?.(c.id)}
                className="flex items-center gap-1.5 text-xs bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 text-[#D4AF37] rounded-full px-3 py-1.5 transition-colors"
              >
                <ChatCircleText size={13} /> Message
              </button>
            </div>
          </div>
        ))}
        {!filtered.length && <p className="text-[#71717A] col-span-full text-center py-10">No clients match your filters.</p>}
      </div>

      {drawerClient && <ClientDetailDrawer client={drawerClient} defaultTab={drawerTab} onClose={() => setDrawerClient(null)} />}
    </div>
  );
}
